import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, invites, clearToken, getToken, setToken, type User } from '../api';

type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: { email: string; password: string; name: string }) => Promise<void>;
  acceptInvite: (data: { token: string; name: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    auth
      .me()
      .then((u) => setUser(u))
      .catch(() => clearToken())
      .finally(() => setIsLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const result = await auth.login({ email, password });
    setToken(result.token);
    setUser(result.user);
  }

  async function signup(data: { email: string; password: string; name: string }) {
    const result = await auth.signup(data);
    setToken(result.token);
    setUser(result.user);
  }

  async function acceptInvite(data: { token: string; name: string; password: string }) {
    const result = await invites.accept(data);
    setToken(result.token);
    setUser(result.user);
  }

  async function logout() {
    try {
      await auth.logout();
    } catch {
      // ignore - logout is best-effort
    }
    clearToken();
    setUser(null);
  }

  async function refresh() {
    try {
      const u = await auth.me();
      setUser(u);
    } catch {
      clearToken();
      setUser(null);
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, isLoading, login, signup, acceptInvite, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth skal bruges inde i en AuthProvider');
  return ctx;
}
